import { createSlice } from '@reduxjs/toolkit';
import {
  offerTypes,
  orderCheckoutTypes,
  orderStateTypes,
  shippingMethodTypes,
  statusTypes,
} from '@/constants/types';
import { checkoutPopupSuccessMessage } from '@/constants/message';
import {
  getAllBankList,
  getAvailableOfferList,
  getBankEmiTenuresList,
  getInitCheckoutSummary,
  getPlaceCheckoutSummary,
  getUpdateCheckoutSummary,
} from '../thunk/checkout-summary-thunk';

const checkoutInitialState = {
  loading: false,
  status: statusTypes.idle,
  postStatus: statusTypes.idle,
  initialStatus: statusTypes.idle, // used to track of init status

  // order token
  orderToken: null,
  // state => init, update, place
  orderState: orderStateTypes.init,

  orderItems: [],
  summary: {
    cart_sub_total: 0,
    cart_sub_total_discount: 0,
    cart_installation_subtotal: 0,
    cart_installation_discount: 0,
    voucher_discount: 0,
    cart_total: 0,
  },

  appliedCoupons: null,
  paymentMethod: null,

  shippingMethod: null,
  selectedShippingMethod: null,

  // common message modal
  message: {
    isSuccess: false,
    isShow: false,
    text: '',
  },

  error: '',

  // bank emi info
  bankEmiList: [],
  currentBankEmi: null,
  fetchEmiDataStatus: statusTypes.idle,

  // available offers info
  availableOfferList: [],
  isPromoCode: false, // not used for now
  availableOfferStatus: offerTypes.none,

  currentPromoCode: null,
  fetchAvailableListStatus: statusTypes.idle,

  // refer code or shop code
  referralCode: null,

  // all bank list
  allBankList: [],
  fetchAllBankListStatus: statusTypes.idle,
};

const initialOrderDetailsState = {
  // after order place get response from BE
  checkoutOrderProcessingStatus: statusTypes.idle,
  redirectUrl: '',
  customerPhoneNumber: '',
  orderId: '',
  // used to remain place order button disable
  isPlaceOrderButtonDisabled: false,
  // used to check order type: buy-now, normal
  type: orderCheckoutTypes.normal,
  error: '',
  message: {
    isSuccess: false,
    isShow: false,
    text: '',
  },
};

const initialState = {
  ...checkoutInitialState,
  ...initialOrderDetailsState,
};

const checkoutSummarySlice = createSlice({
  name: 'checkoutSummary',
  initialState,
  reducers: {
    resetCheckoutSummaryForCache() {
      return initialState;
    },

    selectPaymentMethod: (state, action) => ({
      ...state,
      paymentMethod: action.payload,
    }),

    updatePromoCode: (state, action) => {
      const { code, type } = action.payload;
      let updateData = {};

      if (type === offerTypes.promoCode) {
        updateData = {
          ...updateData,
          isPromoCode: true,
          currentPromoCode: code,
          availableOfferStatus: offerTypes.promoCode,
        };
      } else if (type === offerTypes.couponCode) {
        updateData = {
          ...updateData,
          isPromoCode: false,
          currentPromoCode: code,
          availableOfferStatus: offerTypes.couponCode,
        };
      } else if (type === offerTypes.none) {
        updateData = {
          ...updateData,
          isPromoCode: false,
          currentPromoCode: null,
          availableOfferStatus: offerTypes.none,
        };
      }

      const isSuccess = updateData.currentPromoCode !== null;

      let alertMessage = {
        text: isSuccess ? checkoutPopupSuccessMessage.promoCodeAdded : '',
        isShow: isSuccess,
        isSuccess,
      };

      // if promo code then don't show any success message
      if (type === offerTypes.promoCode) {
        alertMessage = {
          text: '',
          isShow: false,
          isSuccess: false,
        };
      }

      return {
        ...state,
        isPromoCode: updateData.isPromoCode,
        availableOfferStatus: updateData.availableOfferStatus,
        currentPromoCode: updateData.currentPromoCode,
        // message: { ...alertMessage }, // avoid to show message for any coupon
      };
    },

    updateReferCode: (state, action) => {
      const updateData = action?.payload?.length > 0 ? action.payload : '';

      const isSuccess = updateData.length > 0;

      const alertMessage = {
        text: isSuccess
          ? checkoutPopupSuccessMessage.referCodeAdded
          : checkoutPopupSuccessMessage.referCodeRemoved,
        isShow: true,
        isSuccess,
      };

      return {
        ...state,
        referralCode: updateData,
        message: { ...alertMessage },
      };
    },

    resetCheckoutSummaryMessage: (state) => ({
      ...state,
      message: {
        text: '',
        isShow: false,
        isSuccess: false,
      },
    }),

    setCheckoutCommonMessage: (state, action) => {
      const { text, isShow, isSuccess } = action.payload;

      return {
        ...state,
        message: {
          text,
          isShow,
          isSuccess,
        },
      };
    },

    resetPostStatus: (state) => ({
      ...state,
      postStatus: statusTypes.idle,
    }),

    // reset state when checkout button click
    resetCheckoutSummaryState: (state, action) => ({
      ...state,
      orderState: statusTypes.init,
      orderToken: null,

      initialStatus: statusTypes.idle, // reset previous state
      error: '',

      appliedCoupons: null,
      shippingMethod: null,
      selectedShippingMethod: null,
      paymentMethod: null,

      isPromoCode: false,
      availableOfferStatus: offerTypes.none,
      currentPromoCode: null,
      referralCode: null,
      ...initialOrderDetailsState,
      type: action.payload, // can be normal or buy-now
    }),

    resetCheckoutSummaryAfterSucceeded: (state) => ({
      ...state,
      ...checkoutInitialState,
      redirectUrl: '',
    }),

    resetPlaceOrderInfoAfterLogout: (state) => ({
      ...state,

      checkoutOrderProcessingStatus: statusTypes.idle,
      redirectUrl: '',
      error: '',
      message: {
        text: '',
        isShow: false,
        isSuccess: false,
      },
    }),
    resetCheckoutSummaryOnEveryRefresh: (state) => ({
      ...state,
      orderState: orderStateTypes.init,
      error: '',
      orderToken: null,
      message: {
        text: '',
        isShow: false,
        isSuccess: false,
      },
    }),

    updateShippingMethod: (state, action) => ({
      ...state,
      selectedShippingMethod: action.payload,
      referralCode:
        action.payload === shippingMethodTypes.storePickup
          ? null
          : state.referralCode,
    }),
  },
  extraReducers: (builder) => {
    builder
      // initial
      .addCase(getInitCheckoutSummary.pending, (state) => {
        state.status = statusTypes.loading;
        state.initialStatus = statusTypes.loading;
        state.orderToken = null;
        state.error = '';
        state.message = {
          text: '',
          isShow: false,
          isSuccess: false,
        };
      })
      .addCase(getInitCheckoutSummary.fulfilled, (state, action) => {
        const { data } = action.payload;

        state.orderItems = data.items;
        state.orderToken = data.token;
        state.summary = { ...data };

        state.shippingMethod = data.shipping_fee;
        state.selectedShippingMethod = data?.shipping_method;

        state.status = statusTypes.succeeded;
        state.initialStatus = statusTypes.succeeded;
        state.error = '';

        if (data.token) {
          state.orderState = orderStateTypes.update;
        }
      })
      .addCase(getInitCheckoutSummary.rejected, (state, action) => {
        state.status = statusTypes.failed;
        state.initialStatus = statusTypes.failed;
        state.error =
          action.payload || 'Failed to get initial order summary data';
      })

      // update
      .addCase(getUpdateCheckoutSummary.pending, (state) => {
        state.status = statusTypes.loading;
        state.error = '';
        state.message = {
          text: '',
          isShow: false,
          isSuccess: false,
        };
      })
      .addCase(getUpdateCheckoutSummary.fulfilled, (state, action) => {
        const { data } = action.payload;

        state.orderItems = data.items;
        state.orderToken = data.token;
        state.summary = { ...data };

        state.shippingMethod = data.shipping_fee;
        state.selectedShippingMethod = data?.shipping_method;

        state.status = statusTypes.succeeded;
        state.error = '';
      })
      .addCase(getUpdateCheckoutSummary.rejected, (state, action) => {
        const { data } = action.payload;

        state.status = statusTypes.failed;
        state.error =
          data?.error?.message || 'Failed to get update order summary data';

        // const errorResponse = {
        //   text:
        //     data?.error?.message || 'Failed to get update order summary data',
        //   isShow: true,
        //   isSuccess: false,
        // };

        // state.message = { ...errorResponse };
      })

      // final order place
      .addCase(getPlaceCheckoutSummary.pending, (state) => ({
        ...state,
        checkoutOrderProcessingStatus: statusTypes.loading,
        customerPhoneNumber: '',
        orderId: '',
        isPlaceOrderButtonDisabled: true,

        redirectUrl: '',
        error: '',

        message: {
          isSuccess: false,
          isShow: false,
          text: '',
        },
      }))
      .addCase(getPlaceCheckoutSummary.fulfilled, (state, action) => {
        const { data } = action.payload;

        return {
          ...state,
          checkoutOrderProcessingStatus: statusTypes.succeeded,

          redirectUrl: data.redirect_url,
          customerPhoneNumber: data.customer_phone,
          orderId: data.order_id,
          isPlaceOrderButtonDisabled: true,

          error: '',
        };
      })
      .addCase(getPlaceCheckoutSummary.rejected, (state, action) => ({
        ...state,

        isPlaceOrderButtonDisabled: false,
        checkoutOrderProcessingStatus: statusTypes.failed,

        error: action.payload || 'Failed to post order.',
        message: {
          isSuccess: false,
          isShow: true,
          text: action?.payload || 'Failed to post order.',
        },
      }))

      // get all bank emi lists
      .addCase(getBankEmiTenuresList.pending, (state) => {
        state.fetchEmiDataStatus = statusTypes.loading;
        state.error = '';
      })
      .addCase(getBankEmiTenuresList.fulfilled, (state, action) => {
        state.fetchEmiDataStatus = statusTypes.succeeded;
        state.bankEmiList = action.payload.data ?? [];
      })
      .addCase(getBankEmiTenuresList.rejected, (state, action) => {
        state.fetchEmiDataStatus = statusTypes.failed;
        state.error = action.payload || 'Failed to get bank emi list.';
      })

      // get all bank lists
      .addCase(getAllBankList.pending, (state) => {
        state.fetchAllBankListStatus = statusTypes.loading;
        state.error = '';
      })
      .addCase(getAllBankList.fulfilled, (state, action) => {
        state.fetchAllBankListStatus = statusTypes.succeeded;
        state.allBankList = action.payload.data ?? [];
      })
      .addCase(getAllBankList.rejected, (state, action) => {
        state.fetchAllBankListStatus = statusTypes.failed;
        state.error = action.payload || 'Failed to get bank emi list.';
      })

      // get all available lists
      .addCase(getAvailableOfferList.pending, (state) => {
        state.fetchAvailableListStatus = statusTypes.loading;
        state.error = '';
      })
      .addCase(getAvailableOfferList.fulfilled, (state, action) => {
        state.fetchAvailableListStatus = statusTypes.succeeded;
        state.availableOfferList = action.payload.data ?? [];
      })
      .addCase(getAvailableOfferList.rejected, (state, action) => {
        state.fetchAvailableListStatus = statusTypes.failed;
        state.error = action.payload || 'Failed to get bank emi list.';
      });
  },
});

export const {
  resetShippingInfoForAuthenticateUser,
  resetShippingInfoAfterOrderSucceeded,

  // only used
  selectPaymentMethod,
  selectCurrentAvailableOffer,
  updatePromoCode,
  updateReferCode,
  resetCheckoutSummaryAfterSucceeded,
  resetCheckoutSummaryState,
  setCheckoutCommonMessage,
  resetCheckoutSummaryMessage,
  resetPlaceOrderInfoAfterLogout,
  resetCheckoutSummaryForCache,
  resetCheckoutSummaryOnEveryRefresh,
  updateShippingMethod,
} = checkoutSummarySlice.actions;

export default checkoutSummarySlice.reducer;
